import type { TPractice } from "@/api/models/practice/practice";
import type { TPracticeStaff } from "@/api/models/practice/practiceStaff";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import type { TPatient } from "@/api/models/patient/patient";
import { createPathParam, getPathParam } from "@/api/shared/crud.ts";
import { createService } from "@/api/shared/service";
import { API_CONFIG, EPatientLinkedEntity } from "./types";

export { API_CONFIG, EPatientLinkedEntity, type TProviderKindKey } from "./types";

export const isPatientLinkedEntity = (id: unknown): id is EPatientLinkedEntity => {
  return typeof id === "string" && (Object.values(EPatientLinkedEntity) as string[]).includes(id);
};

export type ProviderListByKind = {
  [K in keyof typeof API_CONFIG]: (typeof API_CONFIG)[K]["typeOfEntity"] extends "practice"
    ? TPractice[]
    : TPracticeStaff[];
};

type LinkPayload<K extends keyof typeof API_CONFIG> = {
  [P in (typeof API_CONFIG)[K]["payloadKey"]]: string | null;
};

function makeLinkPayload<K extends keyof typeof API_CONFIG>(
  kind: K,
  targetId: string | null
): LinkPayload<K> {
  const cfg = API_CONFIG[kind];
  return { [cfg.payloadKey]: targetId } as LinkPayload<K>;
}

const SLUG = "patient";

const LinkService = createService(SLUG, {
  listProviders<K extends keyof typeof API_CONFIG>(
    patientId: string,
    kind: K,
    queries?: AxiosRequestConfig["params"]
  ): Promise<AxiosResponse<ProviderListByKind[K]>> {
    const cfg = API_CONFIG[kind];
    return getPathParam<ProviderListByKind[K], { id: string }>(`${SLUG}/{id}/${cfg.listPath}`)(
      { id: patientId },
      queries
    );
  },

  linkProvider<K extends keyof typeof API_CONFIG>(
    patientId: string,
    kind: K,
    targetId: string | null
  ): Promise<AxiosResponse<TPatient>> {
    const cfg = API_CONFIG[kind];
    return createPathParam<TPatient, { id: string }, LinkPayload<K>>(
      `${SLUG}/{id}/${cfg.linkPath}`
    )({ id: patientId }, makeLinkPayload(kind, targetId));
  },
});

export default LinkService;
